import { computed, shallowRef, watch } from 'vue'
import { cAF, rAF } from '@jdesign-vue/utils'

import type { Ref } from 'vue'
import type { Alignment as ScrollStrategy } from '@jdesign-vue/components/virtual-list'
import type { TableV2Props } from '../table'
import type { TableGridInstance } from '../table-grid'

export type ScrollPos = { scrollLeft: number; scrollTop: number }
type GridInstanceRef = Ref<TableGridInstance | undefined>

type UseScrollBarProps = {
  mainTableRef: GridInstanceRef
  leftTableRef: GridInstanceRef
  rightTableRef: GridInstanceRef

  isScrolling: Ref<boolean>
  onMaybeEndReached: () => void
}

export type { ScrollStrategy }

// 滚动停止后多长时间内仍保留 isScrolling=true，避免抖动
const SCROLL_IDLE_DELAY = 150

export const useScrollbar = (
  props: TableV2Props,
  {
    mainTableRef,
    leftTableRef,
    rightTableRef,
    isScrolling,
    onMaybeEndReached,
  }: UseScrollBarProps
) => {
  // 把 scrollLeft / scrollTop 拆成两个独立 ref，
  // 这样消费方只读 scrollLeft（左右固定列阴影）时，scrollTop 变化不会触发其重渲染。
  const scrollLeft = shallowRef(0)
  const scrollTop = shallowRef(0)
  // 兼容老 API：暴露聚合的 scrollPos computed
  const scrollPos = computed<ScrollPos>(() => ({
    scrollLeft: scrollLeft.value,
    scrollTop: scrollTop.value,
  }))

  let idleTimer: ReturnType<typeof setTimeout> | null = null
  let endReachedHandle: number | null = null

  const markScrolling = () => {
    if (!isScrolling.value) isScrolling.value = true
    if (idleTimer) clearTimeout(idleTimer)
    idleTimer = setTimeout(() => {
      isScrolling.value = false
      idleTimer = null
    }, SCROLL_IDLE_DELAY)
  }

  /**
   * @param source - 'main' 表示来源是主表自身的滚动事件，避免回写主表造成冗余调用
   */
  function doScroll(params: ScrollPos, source?: 'main') {
    const { scrollLeft: nextLeft, scrollTop: nextTop } = params

    if (source !== 'main') {
      mainTableRef.value?.scrollTo(params)
    } else {
      // 即使 source === 'main'，也需要同步表头滚动
      mainTableRef.value?.scrollToLeft(nextLeft)
    }
    leftTableRef.value?.scrollToTop(nextTop)
    rightTableRef.value?.scrollToTop(nextTop)
  }

  // methods
  function scrollTo(params: ScrollPos, source?: 'main') {
    // 只在变化时才赋值，避免无意义的依赖触发
    if (scrollLeft.value !== params.scrollLeft)
      scrollLeft.value = params.scrollLeft
    if (scrollTop.value !== params.scrollTop) scrollTop.value = params.scrollTop
    markScrolling()

    doScroll(params, source)
  }

  function scrollToTop(nextTop: number) {
    if (scrollTop.value === nextTop) return
    scrollTop.value = nextTop
    markScrolling()

    doScroll({ scrollLeft: scrollLeft.value, scrollTop: nextTop })
  }

  function scrollToLeft(nextLeft: number) {
    if (scrollLeft.value === nextLeft) return
    scrollLeft.value = nextLeft
    markScrolling()

    mainTableRef.value?.scrollTo?.({
      scrollLeft: nextLeft,
      scrollTop: scrollTop.value,
    })
  }

  function onScroll(params: ScrollPos) {
    // grid 自己的 scroll 事件传上来，回写主表会走一次 noop 的 scrollTo，省掉
    scrollTo(params, 'main')
    props.onScroll?.(params)
  }

  function onVerticalScroll({ scrollTop: next }: ScrollPos) {
    if (next !== scrollTop.value) scrollToTop(next)
  }

  function scrollToRow(row: number, strategy: ScrollStrategy = 'auto') {
    mainTableRef.value?.scrollToRow(row, strategy)
  }

  // When scrollTop changes, maybe reaching the bottom
  // rAF 节流，避免每个滚动事件都重新跑一遍尺寸 computed
  watch(scrollTop, (cur, prev) => {
    if (cur <= prev) return
    if (endReachedHandle != null) cAF(endReachedHandle)
    endReachedHandle = rAF(() => {
      endReachedHandle = null
      onMaybeEndReached()
    })
  })

  return {
    scrollPos,
    scrollLeft,
    scrollTop,

    scrollTo,
    scrollToLeft,
    scrollToTop,
    scrollToRow,
    onScroll,
    onVerticalScroll,
  }
}
